import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserModel } from '@bp-core/src/lib/proto/common-message.pb';
import { GetConfigParam } from '@bp-core/src/lib/proto/config-param.pb';
import { ConfigParamGrpcServiceClient } from '@bp-core/src/lib/proto/config-param.pbsc';
import { MeFinancialEntityModel } from '@bp-core/src/lib/proto/portal-user.pb';
import { PortalUserGrpcServiceClient } from '@bp-core/src/lib/proto/portal-user.pbsc';
import { GetSummaryResponse } from '@bp-core/src/lib/proto/user.pb';
import { UserGrpcServiceClient } from '@bp-core/src/lib/proto/user.pbsc';
import { Empty } from '@ngx-grpc/well-known-types';
import { PatrimonySummary } from 'bp/modules/dashboard/models/patrimony-summary';
import dayjs from 'dayjs';
import { AuthService } from 'projects/bp-core/src/lib/services/portal/auth.service';
import {
  ReplaySubject,
  combineLatest,
  filter,
  lastValueFrom,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { BpCodes } from '../models/config-code.model';
import { CurrentStepInfo } from '../models/current-step';
import { Step } from '../models/first-step.enum';
import { UserAdvisorReassignmentConditions } from '../models/user-aditional-info';
import { UserFinancialEntityInfo } from '../models/user-financial-entity.model';
import { PortalConfigParamService } from './portal-config-param.service';

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private currentStepSubject = new ReplaySubject<CurrentStepInfo>(1);
  public readonly currentStep$ = this.currentStepSubject.asObservable();
  private user: UserModel;
  private totalSteps = 4;
  private financialEntities: MeFinancialEntityModel[];
  private signedContractsEntity: MeFinancialEntityModel[] | undefined;
  private financialEntityInfoSubject = new ReplaySubject<UserFinancialEntityInfo>(1);
  private patrimonySummarySubject = new ReplaySubject<PatrimonySummary>(1);
  private patrimonySummary: PatrimonySummary;
  private showSemiLiquidFlowByUser = false;
  public readonly financialEntityInfo$ = this.financialEntityInfoSubject.asObservable();
  public readonly patrimonySummary$ = this.patrimonySummarySubject.asObservable();
  private bpusEntity: MeFinancialEntityModel | undefined;

  private advisorSubject = new ReplaySubject<UserModel>(1);

  private listIncomePatrimonySegmentation: {
    incomeCode: string;
    patrimonyCode: string;
    segmentation: string;
  }[] = [];
  public readonly advisor$ = this.advisorSubject.asObservable();
  constructor(
    public router: Router,
    private portalUserGrpcServiceClient: PortalUserGrpcServiceClient,
    private authService: AuthService,
    private userGrpcServiceClient: UserGrpcServiceClient,
    private configParamGrpcServiceClient: ConfigParamGrpcServiceClient,
    private portalConfigParamService: PortalConfigParamService,
  ) {
    this.initializeService();
  }
  private initializeService() {
    this.authService.userSubject$
      .pipe(
        switchMap(currentUser => {
          if (currentUser) {
            this.user = currentUser;
            // Retorna el observable de getMeFinancialEntities
            return combineLatest([
              this.portalUserGrpcServiceClient.getMeFinancialEntities(new Empty()).pipe(take(1)),
              this.userGrpcServiceClient.getMeSummary(new Empty()),
              this.portalConfigParamService.get(BpCodes.listIncomePatrimonySegmentation),
              this.portalConfigParamService.get(BpCodes.showSemiLiquidFlow, currentUser.id),
            ]);
          } else {
            // Si no hay un usuario actual, emite un observable que completa inmediatamente
            return of([null]);
          }
        }),
      )
      .pipe(filter(([user]) => user !== null))
      .subscribe(
        async ([
          financialEntities,
          summaryResponse,
          listIncomePatrimonySegmentation,
          showSemiLiquidFlowByUser,
        ]) => {
          this.handleFinancialEntities(financialEntities?.values);

          this.listIncomePatrimonySegmentation = this.mapListIncomePatrimonySegmentation(
            listIncomePatrimonySegmentation,
          );
          this.patrimonySummary = this.getMeSummary(summaryResponse!);
          this.showSemiLiquidFlowByUser = showSemiLiquidFlowByUser === 'true';
          // construimos y emitimos el CurrentStepInfo
          const currentStepInfo = await this.calculateCurrentStepInfo();

          this.patrimonySummarySubject.next(this.patrimonySummary);

          this.currentStepSubject.next(currentStepInfo);

          // Get current user advisor
          this.userGrpcServiceClient.getMeAdvisor(new Empty()).subscribe(advisor => {
            this.advisorSubject.next(advisor);
          });
        },
      );
  }
  private mapListIncomePatrimonySegmentation(
    listIncomePatrimonySegmentation: string | null,
  ): { incomeCode: string; patrimonyCode: string; segmentation: string }[] {
    if (listIncomePatrimonySegmentation) {
      return JSON.parse(listIncomePatrimonySegmentation);
    } else {
      return [];
    }
  }

  private handleFinancialEntities(financialEntities: MeFinancialEntityModel[] | undefined) {
    if (financialEntities) {
      this.financialEntities = financialEntities;
      this.bpusEntity = financialEntities?.find(
        fe => fe.userFinancialEntity?.financialEntity?.uuid === 'bp-us',
      );
      // filtramos los contratos firmados
      this.signedContractsEntity = this.financialEntities.filter(value => value.contract?.signed);

      this.financialEntityInfoSubject.next({
        signedContractsEntity: this.signedContractsEntity,
        financialEntities: this.financialEntities,
      });
    }
  }

  async calculateCurrentStepInfo(user = this.user): Promise<CurrentStepInfo> {
    let currentStepInfo: CurrentStepInfo = {
      currentStep: Step.FIRST,
      canDeposit: false,
      canTransfer: false,
      canWithdraw: false,
      progress: 0,
      extraConditions: {
        emailConfirmed: user.emailVerified,
        hasGoals: user.goals?.length! > 0,
        showSemiLiquidFlow: false,
      },
    };
    // Puede invertir en semiliquidos merge
    currentStepInfo.extraConditions!.showSemiLiquidFlow = await this.showSemiliquidFlow();
    // Esta en el primer paso (segundo paso para entidades)
    if (!user.firstSteps?.skipedAdvisor?.value && !user.firstSteps?.scheduledAdvisor?.value) {
      return currentStepInfo;
    }

    // Esta en el segundo paso (solo para usuarios)
    if (!user.icValidated && user.userType !== 'entity') {
      return this.updateCurrentStepInfo(currentStepInfo, Step.SECOND);
    }

    // Esta en el tercer paso
    if (!user.firstSteps?.contract?.value || !user.emailVerified) {
      return this.updateCurrentStepInfo(currentStepInfo, Step.THIRD);
    }

    // Esta en el cuarto paso
    if (!user.firstSteps.invested?.value) {
      currentStepInfo = this.updateCurrentStepInfo(currentStepInfo, Step.FOURTH);
      if (
        currentStepInfo.extraConditions?.emailConfirmed &&
        currentStepInfo.extraConditions?.hasGoals
      ) {
        currentStepInfo.canDeposit = true;
        currentStepInfo.canTransfer = true;
        currentStepInfo.canWithdraw = true;
      }
      return currentStepInfo;
    }

    // Acabo el proceso
    return this.updateCurrentStepInfo(currentStepInfo, Step.DONE, true);
  }
  /**
   * Obtiene el resumen de patrimonio del usuario, tanto el total como de las metas bpUs
   *
   * @private
   * @param {GetSummaryResponse} summaryResponse
   * @return {*}  {PatrimonySummary}
   * @memberof UserStateService
   */
  private getMeSummary(summaryResponse: GetSummaryResponse): PatrimonySummary {
    const patrimonySummary: PatrimonySummary = {
      totalPatrimony: {
        entity: 'all',
        balance: summaryResponse.balance,
        netDeposit: summaryResponse.netDeposit,
        variation: summaryResponse.balance - summaryResponse.netDeposit,
        currency: this.user?.currency,
      },
    };

    // Obtener metas bpus
    const bpUsGoals = this.user?.goals?.filter(
      goal => goal.financialEntity?.uuid === 'bp-us' && goal.archived == false,
    );
    const currencyBpUs = this.bpusEntity?.userFinancialEntity?.financialEntity?.defaultCurrency;

    if (bpUsGoals && bpUsGoals.length > 0) {
      patrimonySummary.betterplanUsPatrimony = {
        entity: bpUsGoals[0].financialEntity?.uuid!,
        balance: bpUsGoals?.reduce((acc, goal) => acc + goal.currentCapital, 0)!,
        netDeposit: bpUsGoals?.reduce((acc, goal) => acc + goal.currentContribution, 0)!,
        variation: bpUsGoals?.reduce(
          (acc, goal) => acc + goal.currentCapital - goal.currentContribution,
          0,
        )!,
        currency: currencyBpUs /*bpUsGoals[0].currency*/,
      };
    }
    return patrimonySummary;
  }

  private updateCurrentStepInfo(
    currentStepInfo: CurrentStepInfo,
    currentStep: Step,
    canOperate = false,
  ): CurrentStepInfo {
    // Si alguna vez existe una diferencia entre canDeposit, canTransfer y canWithdraw
    return {
      ...currentStepInfo,
      currentStep,
      progress: currentStep === Step.DONE ? 100 : this.getProgress(currentStep),
      canDeposit: canOperate,
      canTransfer: canOperate,
      canWithdraw: canOperate,
    };
  }

  private getProgress(currentStep: Step): number {
    return (currentStep - 1) / this.totalSteps;
  }

  private async showSemiliquidFlow(): Promise<boolean> {
    // Si el usuario tiene permisos para ver el flujo de semiliquidos
    if (this.showSemiLiquidFlowByUser) {
      return true;
    }
    const hasVectorSignedContract = this.signedContractsEntity?.find(
      fe => fe.userFinancialEntity?.financialEntity?.uuid === 'vector',
    );
    // Si el usuario no tiene contrato firmado con vector, no puede ver el flujo de semiliquidos
    if (!hasVectorSignedContract) {
      return false;
    }

    const minimumAumToShowSemiLiquidFlow = await lastValueFrom(
      this.portalConfigParamService.get(BpCodes.minimumAumToShowSemiLiquidFlow),
    );
    const segmentToShowSemiLiquidFlow = (
      await lastValueFrom(this.portalConfigParamService.get(BpCodes.segmentToShowSemiLiquidFlow))
    ).split(',');

    const isSegmentToShowSemiLiquidFlow = segmentToShowSemiLiquidFlow.includes(
      this.user.potencialPlan?.uuid || '',
    );

    // this.listIncomePatrimonySegmentation.find(
    //   segment =>
    //     segment.incomeCode === this.user.information?.incomeCode?.value &&
    //     segment.patrimonyCode == this.user.information.patrimonyCode?.value &&
    //     segment.segmentation === segmentToShowSemiLiquidFlow,
    // );
    // Si el usuario tiene un patrimonio neto mayor o igual al minimo para ver el flujo de semiliquidos o si esta en el segmento que puede ver el flujo de semiliquidos
    if (
      this.patrimonySummary.totalPatrimony.netDeposit >= +minimumAumToShowSemiLiquidFlow ||
      isSegmentToShowSemiLiquidFlow
    ) {
      return true;
    }
    return false;
  }

  getUserAdvisorReassignmentConditions() {
    return combineLatest([
      this.userGrpcServiceClient.getMeInformation(new Empty()),
      this.configParamGrpcServiceClient.get(
        new GetConfigParam({ code: 'ADVISOR-REASSIGNMENT-LIMIT-DATE' }),
      ),
    ]).pipe(
      take(1),
      map(([userInformation, advisorReassignmentLimitDate]) => {
        const userCreationDateDayjs = dayjs.unix(+this.user?.created?.seconds!);
        const advisorReassignmentLimitDateDayjs = dayjs(advisorReassignmentLimitDate.value);

        // console.warn(userCreationDateDayjs.toDate());
        // console.warn(advisorReassignmentLimitDateDayjs.toDate());

        const result: UserAdvisorReassignmentConditions = {
          wasCreatedBeforeAdvisorReassignmentByIncomeAndPatrimony: userCreationDateDayjs.isBefore(
            advisorReassignmentLimitDateDayjs,
          ),
          hasIncomeAndPatrimony:
            userInformation.incomeCode?.value && userInformation.patrimonyCode?.value
              ? true
              : false,
        };

        return result;
      }),
    );
  }

  userHasAlreadyInvestedWithVector(): boolean {
    return this.user.goals?.find(
      g => g.financialEntity?.uuid === 'vector' && g.haveDeposited && g.haveTransactionsProcessed,
    )
      ? true
      : false;
  }

  userFirstVectorInvesmentDate(): Date | null {
    const vectorTransactionSecondArray = this.user.goals
      ?.filter(
        g => g.financialEntity?.uuid === 'vector' && g.haveDeposited && g.haveTransactionsProcessed,
      )
      .map(g => g.firstTransactionDate?.seconds);

    if (vectorTransactionSecondArray && vectorTransactionSecondArray?.length > 0) {
      const firstTransactionDateSeconds = vectorTransactionSecondArray.reduce(
        (oldestFirstTransactionDateSeconds, firstTransactionDateSeconds) => {
          if (oldestFirstTransactionDateSeconds && firstTransactionDateSeconds) {
            return +firstTransactionDateSeconds < +oldestFirstTransactionDateSeconds
              ? firstTransactionDateSeconds
              : oldestFirstTransactionDateSeconds;
          } else {
            return '';
          }
        },
      );

      if (firstTransactionDateSeconds) {
        return dayjs.unix(+firstTransactionDateSeconds).toDate();
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}
